/*
 * @providesModule ImageHeaderModal
 */
import React, {useState} from 'react';
import styles from './ImageHeaderModal.style';
import ClientPropertyConstants from 'ClientPropertyConstants';
import Label from 'Label';
import Optional from 'Optional';
import Sizes from 'Sizes';
import SkillzButtonCoordinator, {getButtonCoordinatorProps} from 'SkillzButtonCoordinator';
import SkillzImage from 'SkillzImage';
import SkillzSVG from 'SkillzSVG';
import {View, ViewStyle, StyleProp, TextStyle, TouchableOpacity, Platform} from 'react-native';
import {noop} from 'lodash';

interface TestIDs {
  MAIN_CONTAINER: string;
  IMAGE: string;
  TITLE: string;
  BODY: string,
  PRIMARY_BUTTON: string;
  SECONDARY_BUTTON?: string;
  CHECKBOX: string;
  CHECKBOX_COPY: string;
}

interface CustomStyles {
  mainContainer?: any | undefined;
  imageContainer?: any | undefined;
}

type Props = {
  imageHeader?: string,
  imageAspectRatio?: number, // required with an Image
  imageWidth?: number,
  title?: string,
  children?: React.ReactElement, // render arbitrary content instead of just text
  text?: string, // not required if using 'children' prop
  buttonTextPrimary:string,
  onPressPrimary: (toggledOn?: boolean)=> void,
  buttonTextSecondary?: string,
  onPressSecondary?: ()=> void,
  bodyTextStyle?: StyleProp<TextStyle>,
  toggleText?: string, // If not provided, the toggle box will be omitted
  toggleDefaultState?: boolean,
  backdropShadowEnabled?: boolean,
  customStyles?: StyleProp<CustomStyles>,
  testIDs: TestIDs,
  imageStyle?: StyleProp<View>,
  secondaryCTAType?: 'Secondary' | 'Tertiary',
}


const TEST_IDS = {
  screen: 'modal_screen',
  image: 'modal_image',
  header: 'modal_header',
  body: 'modal_body',
  acceptCTA: 'modal_accept_cta',
  declineCTA: 'modal_decline_cta',
  checkboxSelected: 'modal_checkbox_selected',
  checkboxUnselected: 'modal_checkbox_unselected',
  checkboxCopy: 'modal_checkbox_copy',
};

const getV1ButtonExtraProps = (buttonStyle: StyleProp<ViewStyle>, titleStyle: StyleProp<TextStyle>) => {
  return {
    buttonStyle,
    titleStyle,
  };
};

const getV2ButtonExtraProps = (containerStyle: ViewStyle, style?: Object, type?: string, size?: string) => {
  const optionalPropsObj = {};
  if (style) { optionalPropsObj['style'] = style; }
  if (type) { optionalPropsObj['type'] = type; }
  if (size) { optionalPropsObj['size'] = size; }
  return {
    containerStyle,
    ...optionalPropsObj,
  };
};

const ImageHeaderModal = (props: Props) => {
  const [toggled, setToggled] = useState(!!props.toggleDefaultState);
  const {imageHeader, title, text, children, buttonTextPrimary, onPressPrimary, buttonTextSecondary, onPressSecondary, customStyles, testIDs} = props;
  const isTungsten = ClientPropertyConstants.isCoreLoopTungstenEnabled();
  const titleStyle = isTungsten ? styles.titleV2 : styles.titleV1;
  const textStyle = isTungsten ? styles.textV2 : styles.textV1;
  const toggleTextStyle = isTungsten ? styles.toggleTextV2 : styles.toggleTextV1;

  const renderToggle = (): React.ReactElement | null => {
    if (!props.toggleText) {
      return null;
    }

    const checkboxTestID = toggled ? (testIDs ? testIDs?.CHECKBOX + '_selected' : TEST_IDS.checkboxSelected)
      : (testIDs ? testIDs?.CHECKBOX + '_unselected' : TEST_IDS.checkboxUnselected);
    return (
      <TouchableOpacity style={styles.toggleContainer}
                        onPress={() => { setToggled(!toggled); }}
                        testID={checkboxTestID}>
        <View style={toggled ? styles.selectedToggle : styles.unselectedToggle}>
          <SkillzSVG style={styles.checkMark}
                     source={'icon_checkmark.svg'}
                     tintColor={'#FFFFFF'}/>
        </View>
        <Label style={toggleTextStyle}
               numberOfLines={1}
               testID={testIDs?.CHECKBOX_COPY ?? TEST_IDS.checkboxCopy}>
          {props.toggleText}
        </Label>
      </TouchableOpacity>
    );
  };

  const shadowStyle = props.backdropShadowEnabled ? {
    backgroundColor: 'rgba(0,0,0,0.6)',
  } : {};
  const containerStyle = [styles.container,
    props.secondaryCTAType === 'Secondary' || !isTungsten ? {} : styles.containerTertiaryButton,
    customStyles?.mainContainer,
  ];

  const secondaryButtonColor  = customStyles?.secondaryButton?.color || '';
  const borderColor = isTungsten && props.secondaryCTAType == 'Secondary' ? {color: secondaryButtonColor} : {};
  const padder = isTungsten && buttonTextSecondary ? {height: 20} : {height: 32};

  // Props for both buttons for both Silver and Tungsten
  const primaryButtonProps = getButtonCoordinatorProps(
    buttonTextPrimary,
    onPressPrimary.bind(this, toggled),
    testIDs?.PRIMARY_BUTTON ?? TEST_IDS.acceptCTA,
    getV1ButtonExtraProps(
      [styles.buttonV1, customStyles?.primaryButton ?? {}],
      [customStyles?.primaryButton ?? {}]
    ),
    getV2ButtonExtraProps({...styles.buttonV2}),
  );
  const secondaryButtonProps = getButtonCoordinatorProps(
    buttonTextSecondary ?? '',
    onPressSecondary ?? noop,
    testIDs?.SECONDARY_BUTTON ?? TEST_IDS.declineCTA,
    getV1ButtonExtraProps(
      [styles.buttonSecondV1, customStyles?.secondaryButton ?? {}],
      [styles.buttonTitleV1, customStyles?.secondaryButton ?? {}]
    ),
    getV2ButtonExtraProps(
      {...styles.buttonSecondV2},
      {...borderColor, themeType: 'Light', textColor: secondaryButtonColor},
      props.secondaryCTAType ?? 'Secondary',
      props.secondaryCTAType === 'Secondary' ? 'L' : 'M'
    )
  );

  return (
    <View style={[{width: Sizes.screenWidth, height: Sizes.screenHeight}, shadowStyle]}>
      <View style={styles.outerContainer}
            testID={testIDs?.MAIN_CONTAINER ?? TEST_IDS.screen}>
        <View style={containerStyle}>
          <Optional test={imageHeader}>
            <View testID={testIDs?.IMAGE ?? TEST_IDS.image}
                  style={[styles.imageContainer, customStyles?.imageContainer]}>
              <SkillzImage style={[styles.image, {aspectRatio: props.imageAspectRatio}, {width: props.imageWidth ?? 300}]}
                           source={imageHeader!}
                           resizeMode={'cover'}/>
            </View>
          </Optional>
          <Optional test={!children}>
            <View style={styles.padder}/>
          </Optional>
          <Optional test={title}>
            <Label testID={testIDs?.TITLE ?? TEST_IDS.header}
                   style={[titleStyle, customStyles?.title]}
                   numberOfLines={2}
                   adjustsFontSizesToFit={false}>{title}</Label>
          </Optional>
          <Optional test={text}>
            <Label adjustsFontSizeToFit={false}
                   numberOfLines={0}
                   testID={testIDs?.BODY ?? TEST_IDS.body}
                   style={[textStyle, props.bodyTextStyle]}>{text}</Label>
          </Optional>
          <Optional test={children}>
            <View style={styles.bodyContainer}>
              {children}
            </View>
          </Optional>
          {renderToggle()}
          <View style={padder}/>
          <SkillzButtonCoordinator {...primaryButtonProps} />
          <Optional test={buttonTextSecondary}>
            <SkillzButtonCoordinator {...secondaryButtonProps} />
          </Optional>
        </View>
      </View>
    </View>
  );
};

if (Platform.OS !== 'web') {
  ImageHeaderModal.defaultProps = {
  backdropShadowEnabled: true,
 };
}

export default ImageHeaderModal;
